import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldCheck, Lock, CheckCircle, WarningCircle, ArrowLeft, SpinnerGap } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const validatePassword = (password) => {
    const checks = {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    return checks;
  };

  const checks = validatePassword(newPassword);
  const isValid = Object.values(checks).every(Boolean) && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (!isValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    setLoading(true);
    try {
      await API.post('/auth/reset-password', { token, new_password: newPassword });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-sm shadow-xl overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-emerald-500 to-emerald-600" />
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-emerald-600" />
              </div>
              <h1 className="text-2xl font-black text-navy-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Password Reset Complete
              </h1>
              <p className="text-navy-500 mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm">
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !token) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-sm shadow-xl overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-red-500 to-red-600" />
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <WarningCircle size={32} className="text-red-600" />
              </div>
              <h1 className="text-2xl font-black text-navy-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Invalid Reset Link
              </h1>
              <p className="text-navy-500 mb-6">{error}</p>
              <Link to="/login">
                <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm">
                  <ArrowLeft size={16} className="mr-2" /> Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded bg-orange-500 flex items-center justify-center">
            <ShieldCheck size={24} className="text-white" />
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            IAG Platform
          </h1>
        </div>

        <div className="bg-white rounded-sm shadow-xl overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-orange-500 to-orange-600" />
          <div className="p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                <Lock size={24} className="text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Set New Password
              </h2>
              <p className="text-sm text-navy-500 mt-1">Enter your new password below</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 rounded-sm"
                  placeholder="Enter new password"
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 rounded-sm"
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>

              {/* Password requirements */}
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <p className="text-xs font-semibold text-navy-500 uppercase tracking-wider mb-2">Password Requirements</p>
                <div className="grid grid-cols-2 gap-1">
                  <div className={`text-xs flex items-center gap-1 ${checks.length ? 'text-emerald-600' : 'text-navy-400'}`}>
                    <CheckCircle size={12} /> 12+ characters
                  </div>
                  <div className={`text-xs flex items-center gap-1 ${checks.uppercase ? 'text-emerald-600' : 'text-navy-400'}`}>
                    <CheckCircle size={12} /> Uppercase letter
                  </div>
                  <div className={`text-xs flex items-center gap-1 ${checks.lowercase ? 'text-emerald-600' : 'text-navy-400'}`}>
                    <CheckCircle size={12} /> Lowercase letter
                  </div>
                  <div className={`text-xs flex items-center gap-1 ${checks.number ? 'text-emerald-600' : 'text-navy-400'}`}>
                    <CheckCircle size={12} /> Number
                  </div>
                  <div className={`text-xs flex items-center gap-1 ${checks.special ? 'text-emerald-600' : 'text-navy-400'}`}>
                    <CheckCircle size={12} /> Special character
                  </div>
                  <div className={`text-xs flex items-center gap-1 ${newPassword && confirmPassword && newPassword === confirmPassword ? 'text-emerald-600' : 'text-navy-400'}`}>
                    <CheckCircle size={12} /> Passwords match
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={!isValid || loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm disabled:opacity-50"
                data-testid="reset-password-submit"
              >
                {loading ? (
                  <><SpinnerGap size={16} className="mr-2 animate-spin" /> Resetting...</>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-navy-500 hover:text-orange-500 transition-colors">
                <ArrowLeft size={14} className="inline mr-1" /> Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
